import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Printer, Settings, Info, 
  CheckCircle, XCircle, MapPin, Building, Package, Users
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';
import { formatDateOnlyARG } from '../../lib/utils';

const PrinterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canRead, canUpdate } = usePermissions('micro', 'printers');
  
  const [printer, setPrinter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  
  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [dropdowns, setDropdowns] = useState({ 
    brands: [], status: [], locations: [], suppliers: [], empresas: [], sites: [], tipos: [], clientes: [] 
  });
  const [saving, setSaving] = useState(false);
  const [invalidId, setInvalidId] = useState(false);

  const fetchPrinter = async () => {
    // La BD de algunos clientes tiene registros con id NULL; la ruta llega como "null"/"undefined"
    if (!/^\d+$/.test(String(id))) {
      setInvalidId(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/assets/printers/${id}`);
      setPrinter(response.data);
    } catch (error) {
      toast.error('Error al cargar impresora');
      navigate('/micro/printers');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const response = await api.get('/assets/printers/form/options');
      const data = response.data;
      setDropdowns({
        brands: data.brands || [],
        status: data.statuses || [],
        locations: data.locations || [],
        suppliers: data.suppliers || [],
        empresas: data.empresas || [],
        sites: data.sites || [],
        tipos: data.tipos || [],
        clientes: data.clientes || []
      });
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  useEffect(() => {
    fetchPrinter();
    fetchDropdowns();
  }, [id]);

  const handleEdit = () => {
    setFormData({
      brand_id: printer.brand_id || '',
      empresa_id: printer.empresa_id || '',
      site_id: printer.site_id || '',
      tipo_id: printer.tipo_id || '',
      ip_address: printer.ip_address || '',
      serial_number: printer.serial_number || '',
      asset_tag: printer.asset_tag || '',
      status_id: printer.status_id || '',
      purchase_date: printer.purchase_date || '',
      location_id: printer.location_id || '',
      supplier_id: printer.supplier_id || '',
      cliente_id: printer.cliente_id || '',
      comments: printer.comments || ''
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.ip_address.trim()) {
      toast.error('La dirección IP es requerida');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        tipo_id: formData.tipo_id ? parseInt(formData.tipo_id) : null,
        ip_address: formData.ip_address.trim(),
        serial_number: formData.serial_number || null,
        asset_tag: formData.asset_tag || null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        purchase_date: formData.purchase_date || null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        cliente_id: formData.cliente_id ? parseInt(formData.cliente_id) : null,
        comments: formData.comments || null
      };

      await api.put(`/assets/printers/${id}`, payload);
      toast.success('Impresora actualizada exitosamente');
      setEditModal(false);
      fetchPrinter();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Impresoras</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (invalidId) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto text-center py-16 space-y-4" data-testid="printer-invalid-id">
          <div className="flex justify-center">
            <div className="h-14 w-14 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Info className="h-7 w-7 text-amber-500" />
            </div>
          </div>
          <h1 className="text-xl font-semibold">Impresora sin ID válido</h1>
          <p className="text-muted-foreground">
            Este registro no tiene un ID en la base de datos (id nulo), por lo que no puede abrirse ni
            editarse individualmente. Corregí el registro asignándole un ID para poder gestionarlo.
          </p>
          <Button onClick={() => navigate('/micro/printers')} className="btn btn-primary">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver al listado
          </Button>
        </div>
      </Layout>
    );
  }

  if (!printer) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Impresora no encontrada</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <button 
              onClick={() => navigate('/micro/printers')}
              className="mt-1 p-1 hover:bg-muted rounded"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <Printer className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="page-title font-mono">{printer.ip_address}</h1>
                <p className="text-muted-foreground">{printer.brand_name || 'Sin marca'}</p>
              </div>
            </div>
          </div>
          {canUpdate && (
            <Button onClick={handleEdit} className="btn btn-primary">
              Editar
            </Button>
          )}
        </div>

        {/* Summary Bar */}
        <Card className="card-subtle">
          <CardContent className="py-4">
            <div className="grid grid-cols-4 gap-8">
              <div>
                <span className="text-xs text-muted-foreground block mb-1">ESTADO</span>
                {printer.status_name ? (
                  <span className={`badge ${printer.status_name === 'Activo' ? 'badge-green' : 'badge-red'}`}>
                    {printer.status_name}
                  </span>
                ) : (
                  <span className="badge badge-amber">Sin estado</span>
                )}
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">MARCA</span>
                <span className="font-medium">{printer.brand_name || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">UBICACION</span>
                <span className="text-sm">{printer.location_name || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">PROVEEDOR</span>
                <span className="text-sm">{printer.supplier_name || '-'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="nerve-card p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="flex items-center gap-2" data-testid="tab-general">
              <Settings className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="info" className="flex items-center gap-2" data-testid="tab-info">
              <Info className="h-4 w-4" />
              Informacion
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="mt-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Identificación */}
              <Card className="card-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Printer className="h-4 w-4 text-primary" />
                    Identificacion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">IP</span>
                    <span className="font-mono text-primary">{printer.ip_address}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Numero de Serie</span>
                    <span className="font-mono">{printer.serial_number || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Asset Tag</span>
                    <span className="font-mono">{printer.asset_tag || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Marca</span>
                    <span>{printer.brand_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Tipo</span>
                    <span>{printer.tipo_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Estado</span>
                    {printer.status_name ? (
                      <span className={`badge ${printer.status_name === 'Activo' ? 'badge-green' : 'badge-red'}`}>
                        {printer.status_name}
                      </span>
                    ) : (
                      <span className="badge badge-amber">Sin estado</span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Ubicación y Proveedor */}
              <Card className="card-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Ubicacion y Proveedor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Building className="h-4 w-4" /> Empresa
                    </span>
                    <span>{printer.empresa_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Users className="h-4 w-4" /> Cliente
                    </span>
                    <span>{printer.cliente_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Site
                    </span>
                    <span>{printer.site_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Ubicacion
                    </span>
                    <span>{printer.location_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Package className="h-4 w-4" /> Proveedor
                    </span>
                    <span>{printer.supplier_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Fecha de Compra</span>
                    <span>{printer.purchase_date ? formatDateOnlyARG(printer.purchase_date) : '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Comentarios */}
              {printer.comments && (
                <Card className="card-subtle col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Info className="h-4 w-4 text-primary" />
                      Comentarios
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{printer.comments}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Info Tab */}
          <TabsContent value="info" className="mt-4">
            <Card className="card-subtle">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="h-4 w-4 text-primary" />
                  Informacion del Registro
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">ID</Label>
                    <p className="font-mono">{printer.id}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Brand ID</Label>
                    <p className="font-mono">{printer.brand_id || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status ID</Label>
                    <p className="font-mono">{printer.status_id || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Location ID</Label>
                    <p className="font-mono">{printer.location_id || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Supplier ID</Label>
                    <p className="font-mono">{printer.supplier_id || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        <Dialog open={editModal} onOpenChange={setEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5 text-primary" />
                Editar Impresora
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Direccion IP *</Label>
                  <Input
                    value={formData.ip_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                    placeholder="192.168.1.100"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Numero de Serie</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Asset Tag</Label>
                  <Input
                    value={formData.asset_tag}
                    onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                    placeholder="IMP-001"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <select
                    value={formData.tipo_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipo_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.tipos?.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Marca</Label>
                  <select
                    value={formData.brand_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, brand_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.brands.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <select
                    value={formData.status_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, status_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.status.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Empresa</Label>
                  <select
                    value={formData.empresa_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, empresa_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.empresas?.map(em => (
                      <option key={em.id} value={em.id}>{em.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Site</Label>
                  <select
                    value={formData.site_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.sites?.map(st => (
                      <option key={st.id} value={st.id}>{st.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Ubicacion</Label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, location_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Proveedor</Label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                    className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Cliente</Label>
                <select
                  value={formData.cliente_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, cliente_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="detail-cliente-select"
                >
                  <option value="">Seleccionar...</option>
                  {dropdowns.clientes?.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Comentarios</Label>
                <Input
                  value={formData.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PrinterDetail;
